import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { LocationTabTemplate as template } from './location-tab.template';
import { LocationTabStyles as styles } from './location-tab.styles';
import { Select, TextArea, TextField } from '@genesislcap/foundation-zero';
import { IProjectModalService, ICommonService } from '../../../../../services';
import { clearErrorUtil, displayErrorUtil } from '../../../../../utils';
import { IState, Project } from '../../../../../types';

const name = 'location-tab';

@customElement({
  name,
  template,
  styles,
})
export class LocationTab extends FASTElement {
  @ICommonService commonService: ICommonService;
  @IProjectModalService projectModalService: IProjectModalService;

  // Location Tab Elements
  @observable latitude: TextField;
  @observable longitude: TextField;
  @observable country: Select;
  @observable state: Select;
  @observable county: Select;
  @observable city: Select;
  private project: Project;
  @observable stateContainer: HTMLElement;
  @observable countyContainer: HTMLElement;
  @observable cityContainer: HTMLElement;

  // Location Tab Form Values
  @observable latitudeValue: string = '';
  @observable longitudeValue: string = '';
  @observable countryValue: string = '';
  @observable stateValue: string = '';
  @observable countyValue: string = '';
  @observable cityValue: string = '';

  @observable _allCountries = [];
  @observable _allStates = [];
  @observable _counties = [];
  @observable _cities = [];

  public _requiredFormFields = [];
  private emptyFieldMessage = 'Please fill out this field';

  connectedCallback() {
    super.connectedCallback();

    this._requiredFormFields = [this.country, this.latitude, this.longitude];

    // Update service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);

    this.attachedBlurEvent();
    this.getCountries();
    this.getStates();
  }

  private attachedBlurEvent() {
    this._requiredFormFields.forEach((field) => {
      if (field instanceof Select) {
        field.addEventListener('change', () => this.checkFieldValidity(field));
      }

      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });
  }

  public checkFormValidity() {
    this._requiredFormFields.forEach((field) => {
      this.checkFieldValidity(field);
    });
  }

  /**
   * Check each field's validity and show/remove UI errors
   * @param elm
   */
  public checkFieldValidity(elm: TextField | Select) {
    if (!elm.value) {
      displayErrorUtil(elm);
      this.amendValidList(elm, false);
    } else {
      if (elm === this.latitude) {
        this.validateLatitude();
      } else if (elm === this.longitude) {
        this.validateLongitude();
      } else {
        clearErrorUtil(elm);
        this.amendValidList(elm, true);
      }
    }
  }

  private validateLatitude() {
    let lat = parseFloat(this.latitudeValue);
    if (23.0 <= lat && lat <= 50.0) {
      clearErrorUtil(this.latitude);
      this.amendValidList(this.latitude, true);
    } else {
      let errorMessage = 'The Latitude value input is not within the continental United States';
      displayErrorUtil(this.latitude, errorMessage);
      this.amendValidList(this.latitude, false);
    }
  }

  private validateLongitude() {
    let long = parseFloat(this.longitudeValue);
    if (-125.0 <= long && long <= -66.0) {
      clearErrorUtil(this.longitude);
      this.amendValidList(this.longitude, true);
    } else {
      let errorMessage = 'The Longitude value input is not within the continental United States';
      displayErrorUtil(this.longitude, errorMessage);
      this.amendValidList(this.longitude, false);
    }
  }

  /**
   * Adds and removed fields from the completed fields list and Emits value to parent component
   * @param field
   * @param isValid
   */
  private amendValidList(field: TextField | TextArea | Select, isValid: boolean) {
    if (isValid) {
      this.projectModalService.addToTotalCompletedFields(field);
    } else {
      this.projectModalService.removeFromTotalCompletedFields(field);
    }

    this.checkTabCompletionStatus();
  }

  public countrySelected() {
    setTimeout(() => {
      if (this.countryValue === 'United States') {
        this._requiredFormFields = [
          this.country,
          this.state,
          this.county,
          this.latitude,
          this.longitude,
        ];

        this.projectModalService.addToRequiredFormField([this.state, this.county]);
      } else {
        this._requiredFormFields = [this.country, this.latitude, this.longitude];

        this.projectModalService.removeFromRequiredFormField([this.state, this.county]);
      }

      // If the user country reset values
      if (this.state && this.county && this.city && !this.project) {
        this.stateValue = '';
        this.countyValue = '';
        this.cityValue = '';
        this.projectModalService.removeFromTotalCompletedFields(this.state);
        this.projectModalService.removeFromTotalCompletedFields(this.city);
        this.projectModalService.removeFromTotalCompletedFields(this.county);
      }

      this.attachedBlurEvent();
    }, 500);
  }

  public async stateSelected() {
    this.countyValue = '';
    this.cityValue = '';
    this.projectModalService.removeFromTotalCompletedFields(this.city);
    this.projectModalService.removeFromTotalCompletedFields(this.county);
    this.clearErrors([this.county, this.city]);

    if (this.stateValue) {
      await this.getCounties();
      await this.getCities();
      this.setCountyCity();
    }
  }

  private setCountyCity() {
    if (this.project) {
      setTimeout(() => {
        if (this.project.COUNTY_NAME) {
          this.countyValue = this.project.COUNTY_NAME;
          this.amendValidList(this.county, this.project.COUNTY_NAME ? true : false);
        }
        if (this.project.CITY_NAME) {
          this.cityValue = this.project.CITY_NAME;
        }
      }, 100);
    }
  }

  private clearErrors(fields) {
    fields.forEach((elm) => {
      clearErrorUtil(elm);
    });
  }

  private async getCountries() {
    await this.commonService.getAllCountries().then((response) => {
      if (response.REPLY) {
        this._allCountries = response.REPLY.sort((a, b) =>
          a.COUNTRY_NAME.localeCompare(b.COUNTRY_NAME),
        );
        this.amendValidList(this.country, true);
      }
    });
  }

  public async getStates() {
    await this.commonService.getAllStates().then((response) => {
      if (response.REPLY) {
        this._allStates = response.REPLY;
      }
    });
  }

  public async getCounties() {
    if (this.stateValue) {
      const stateName = JSON.parse(this.stateValue).STATE_NAME;

      await this.commonService.getCounties(stateName).then((response) => {
        if (response.REPLY) {
          this._counties = response.REPLY.sort((a, b) =>
            a.COUNTY_NAME.localeCompare(b.COUNTY_NAME),
          );
        }
      });
    }
  }

  public async getCities() {
    const stateName = JSON.parse(this.stateValue).STATE_NAME;

    await this.commonService.getCities(stateName).then((response) => {
      if (response.REPLY) {
        this._cities = response.REPLY.sort((a, b) => a.CITY_NAME.localeCompare(b.CITY_NAME));
      }
    });
  }

  private checkTabCompletionStatus() {
    const tabCompleted = this._requiredFormFields.every((elm) =>
      this.projectModalService.totalCompletedFields.includes(elm),
    );
    this.$emit('locationTabCompleted', tabCompleted);
  }

  public clearForm() {
    const allFields = [
      this.country,
      this.state,
      this.county,
      this.city,
      this.latitude,
      this.longitude,
    ];

    allFields.forEach((field) => {
      if (field) {
        clearErrorUtil(field);
      }
    });

    this.countryValue = 'United States';
    setTimeout(() => {
      this.countrySelected();
      this.checkFieldValidity(this.country);
    }, 100);

    this.stateValue = '';
    this.countyValue = '';
    this.cityValue = '';
    this.latitudeValue = null;
    this.longitudeValue = null;
    this.project = {} as Project;
    this._requiredFormFields = [this.country, this.latitude, this.longitude];

    this.projectModalService.removeFromRequiredFormField([this.state, this.county]);

    this.$emit('locationTabCompleted', false);
  }

  public async populateForm(project: Project) {
    this.project = project;
    this.addRequiredFieldsToService();

    if (project.COUNTRY_NAME) {
      this.countryValue = project.COUNTRY_NAME;
      if (this.countryValue === 'United States') {
        this.projectModalService.addToRequiredFormField([this.state, this.county]);
      }
      this.amendValidList(this.country, true);
    }

    if (project.STATE_NAME) {
      const stateValueJson = {} as IState;
      stateValueJson.STATE_NAME = project.STATE_NAME;
      stateValueJson.STATE_ABBREVIATION = project.STATE_ABBREVIATION;
      this.stateValue = JSON.stringify(stateValueJson);
      this.amendValidList(this.state, true);
      await this.getCounties();
      await this.getCities();
    }
    this.setCountyCity();
    if (project.LATITUDE) {
      this.latitudeValue = project.LATITUDE ? String(project.LATITUDE) : '';
      this.validateLatitude();
    }
    if (project.LONGITUDE) {
      this.longitudeValue = project.LONGITUDE ? String(project.LONGITUDE) : '';
      this.validateLongitude();
    }
  }

  public addRequiredFieldsToService() {
    // Update service with latest required fields
    this.projectModalService.addToRequiredFormField(this._requiredFormFields);
  }
}
